// src/Pages/Dashboard.jsx

import {
  TbNotes,
  TbX,
  TbCalendar,
  TbCheck,
  TbChevronDown,
  TbUser,
  TbAdjustmentsHorizontal,
} from "react-icons/tb";
import DashboardColumn from "../Components/DashboardColumn";
import DataTable from "../Components/DataTable";
import AuthenticatedLayout from "../Layouts/AuthenticatedLayout";
import TrailerDiagram from "../Components/TrailerDiagram";
import { Notebook } from "lucide-react";
import { useAuth } from "../AuthProvider";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import axios from "axios";

// Currency formatter
const fmtCurrency = (value) => {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 2,
    }).format(value || 0);
  } catch {
    return `$${(value || 0).toFixed(2)}`;
  }
};

const fmtCurrencyCompact = (value) => {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: "USD",
      notation: "compact",
      maximumFractionDigits: 1,
    }).format(value || 0);
  } catch {
    return fmtCurrency(value);
  }
};

// Get Bearer token from localStorage (common keys)
const getAuthToken = () => {
  const candidates = [
    "token",
    "access_token",
    "authToken",
    "auth_token",
    "jwt",
    "bearer",
  ];

  for (const key of candidates) {
    const v = window.localStorage.getItem(key);
    if (v && typeof v === "string" && v.trim().length > 0) return v.trim();
  }

  return null;
};

const RANGE_OPTIONS = [
  { value: "last_7_days", label: "Last 7 Days" },
  { value: "last_15_days", label: "Last 15 Days" },
  { value: "last_month", label: "Last Month" },
  { value: "last_year", label: "Last Year" },
  { value: "max", label: "Max" },
];

const STATUS_OPTIONS = [
  { value: "all", label: "All Statuses" },
  { value: "1", label: "New" },
  { value: "2", label: "Assigned" },
  { value: "3", label: "Confirmed" },
  { value: "4", label: "Picked Up" },
  { value: "5", label: "Dropped Off" },
  { value: "6", label: "Delivered" },
  { value: "7", label: "Billed" },
  { value: "8", label: "Paid" },
  { value: "9", label: "Cancelled" },
  { value: "10", label: "Archived" },
];

const getRangeLabel = (rangeKey) => {
  const found = RANGE_OPTIONS.find((item) => item.value === rangeKey);
  return found ? found.label : "Last 7 Days";
};

function DashboardTrendChart({ chart }) {
  const series = Array.isArray(chart?.series) ? chart.series : [];

  const totals = useMemo(() => {
    return series.reduce(
      (acc, item) => {
        acc.pickups += Number(item.pickups || 0);
        acc.dropoffs += Number(item.dropoffs || 0);
        acc.revenue += Number(item.revenue || 0);
        return acc;
      },
      { pickups: 0, dropoffs: 0, revenue: 0 }
    );
  }, [series]);

  const hasAnyValue = useMemo(() => {
    return series.some(
      (item) =>
        Number(item.pickups || 0) > 0 ||
        Number(item.dropoffs || 0) > 0 ||
        Number(item.revenue || 0) > 0
    );
  }, [series]);

  const chartGeometry = useMemo(() => {
    const width = 980;
    const height = 280;
    const margin = { top: 18, right: 72, bottom: 44, left: 52 };
    const plotWidth = width - margin.left - margin.right;
    const plotHeight = height - margin.top - margin.bottom;

    const maxCount = Math.max(
      1,
      ...series.map((item) =>
        Math.max(Number(item.pickups || 0), Number(item.dropoffs || 0))
      )
    );

    const maxRevenue = Math.max(
      1,
      ...series.map((item) => Number(item.revenue || 0))
    );

    const getX = (index) => {
      if (series.length <= 1) return margin.left + plotWidth / 2;
      return margin.left + (plotWidth * index) / (series.length - 1);
    };

    const getCountY = (value) => {
      const v = Number(value || 0);
      return margin.top + plotHeight - (v / maxCount) * plotHeight;
    };

    const getRevenueY = (value) => {
      const v = Number(value || 0);
      return margin.top + plotHeight - (v / maxRevenue) * plotHeight;
    };

    const pickupsPoints = series.map((item, index) => ({
      x: getX(index),
      y: getCountY(item.pickups),
      label: item.label,
      value: Number(item.pickups || 0),
    }));

    const dropoffsPoints = series.map((item, index) => ({
      x: getX(index),
      y: getCountY(item.dropoffs),
      label: item.label,
      value: Number(item.dropoffs || 0),
    }));

    const revenuePoints = series.map((item, index) => ({
      x: getX(index),
      y: getRevenueY(item.revenue),
      label: item.label,
      value: Number(item.revenue || 0),
    }));

    const toLinePath = (points) => {
      if (!points.length) return "";
      return points
        .map((point, index) =>
          `${index === 0 ? "M" : "L"} ${point.x.toFixed(2)} ${point.y.toFixed(
            2
          )}`
        )
        .join(" ");
    };

    const revenueAreaPath = revenuePoints.length
      ? [
          `M ${revenuePoints[0].x.toFixed(2)} ${(
            margin.top + plotHeight
          ).toFixed(2)}`,
          ...revenuePoints.map(
            (point) => `L ${point.x.toFixed(2)} ${point.y.toFixed(2)}`
          ),
          `L ${revenuePoints[revenuePoints.length - 1].x.toFixed(2)} ${(
            margin.top + plotHeight
          ).toFixed(2)}`,
          "Z",
        ].join(" ")
      : "";

    const gridFractions = [0, 0.25, 0.5, 0.75, 1];

    const tickIndexes = (() => {
      if (series.length <= 6) {
        return series.map((_, index) => index);
      }

      const step = Math.ceil(series.length / 6);
      const indexes = [];

      for (let i = 0; i < series.length; i += step) {
        indexes.push(i);
      }

      if (indexes[indexes.length - 1] !== series.length - 1) {
        indexes.push(series.length - 1);
      }

      return indexes;
    })();

    return {
      width,
      height,
      margin,
      plotWidth,
      plotHeight,
      maxCount,
      maxRevenue,
      gridFractions,
      tickIndexes,
      pickupsPoints,
      dropoffsPoints,
      revenuePoints,
      pickupsPath: toLinePath(pickupsPoints),
      dropoffsPath: toLinePath(dropoffsPoints),
      revenuePath: toLinePath(revenuePoints),
      revenueAreaPath,
    };
  }, [series]);

  return (
    <div className="bg-[#1c1b19] p-4 sm:p-5 rounded-xl shadow-sm text-white border border-zinc-600 mb-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between mb-4">
        <div>
          <h2 className="text-lg sm:text-xl font-bold">Trend Overview</h2>
          <p className="text-sm text-gray-400 mt-1">
            Dynamic chart for pickups, dropoffs and revenue.
          </p>
        </div>

        <div className="flex flex-wrap gap-2 text-xs">
          <span className="px-2.5 py-1 rounded-full bg-zinc-800 border border-zinc-700 text-white">
            {chart?.range_label || "Last 7 Days"}
          </span>
          <span className="px-2.5 py-1 rounded-full bg-zinc-800 border border-zinc-700 text-white">
            {chart?.granularity === "month" ? "Monthly" : "Daily"}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        <div className="bg-zinc-900/40 border border-zinc-700 rounded-xl p-3">
          <div className="text-xs uppercase tracking-wider text-gray-400">
            Pickups
          </div>
          <div className="text-base sm:text-lg font-semibold text-emerald-400 mt-1">
            {totals.pickups}
          </div>
        </div>

        <div className="bg-zinc-900/40 border border-zinc-700 rounded-xl p-3">
          <div className="text-xs uppercase tracking-wider text-gray-400">
            Dropoffs
          </div>
          <div className="text-base sm:text-lg font-semibold text-amber-400 mt-1">
            {totals.dropoffs}
          </div>
        </div>

        <div className="bg-zinc-900/40 border border-zinc-700 rounded-xl p-3">
          <div className="text-xs uppercase tracking-wider text-gray-400">
            Revenue
          </div>
          <div className="text-base sm:text-lg font-semibold text-sky-400 mt-1">
            {fmtCurrency(totals.revenue)}
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-4 text-xs mb-4">
        <div className="flex items-center gap-2">
          <span className="inline-block w-3 h-3 rounded-full bg-emerald-400" />
          <span className="text-gray-300">Pickups</span>
        </div>

        <div className="flex items-center gap-2">
          <span className="inline-block w-3 h-3 rounded-full bg-amber-400" />
          <span className="text-gray-300">Dropoffs</span>
        </div>

        <div className="flex items-center gap-2">
          <span className="inline-block w-3 h-3 rounded-full bg-sky-400" />
          <span className="text-gray-300">Revenue</span>
        </div>
      </div>

      {!series.length || !hasAnyValue ? (
        <div className="min-h-[180px] rounded-xl border border-dashed border-zinc-700 bg-zinc-900/20 flex items-center justify-center text-sm text-gray-400">
          No chart data available for the selected filters.
        </div>
      ) : (
        <div className="w-full overflow-x-auto">
          <div className="min-w-[760px]">
            <svg
              viewBox={`0 0 ${chartGeometry.width} ${chartGeometry.height}`}
              className="w-full h-auto"
              role="img"
              aria-label="Dashboard trend chart"
            >
              <rect
                x="0"
                y="0"
                width={chartGeometry.width}
                height={chartGeometry.height}
                fill="transparent"
                rx="12"
              />

              {chartGeometry.gridFractions.map((fraction, index) => {
                const y =
                  chartGeometry.margin.top +
                  chartGeometry.plotHeight -
                  fraction * chartGeometry.plotHeight;

                const leftValue = Math.round(chartGeometry.maxCount * fraction);
                const rightValue = chartGeometry.maxRevenue * fraction;

                return (
                  <g key={`grid-${index}`}>
                    <line
                      x1={chartGeometry.margin.left}
                      y1={y}
                      x2={chartGeometry.margin.left + chartGeometry.plotWidth}
                      y2={y}
                      stroke="#3f3f46"
                      strokeWidth="1"
                      strokeDasharray="4 4"
                    />
                    <text
                      x={chartGeometry.margin.left - 10}
                      y={y + 4}
                      textAnchor="end"
                      fontSize="11"
                      fill="#a1a1aa"
                    >
                      {leftValue}
                    </text>
                    <text
                      x={
                        chartGeometry.margin.left +
                        chartGeometry.plotWidth +
                        10
                      }
                      y={y + 4}
                      textAnchor="start"
                      fontSize="11"
                      fill="#a1a1aa"
                    >
                      {fmtCurrencyCompact(rightValue)}
                    </text>
                  </g>
                );
              })}

              {chartGeometry.revenueAreaPath && (
                <path
                  d={chartGeometry.revenueAreaPath}
                  fill="rgba(56, 189, 248, 0.10)"
                  stroke="none"
                />
              )}

              {chartGeometry.revenuePath && (
                <path
                  d={chartGeometry.revenuePath}
                  fill="none"
                  stroke="#38bdf8"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              )}

              {chartGeometry.pickupsPath && (
                <path
                  d={chartGeometry.pickupsPath}
                  fill="none"
                  stroke="#34d399"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              )}

              {chartGeometry.dropoffsPath && (
                <path
                  d={chartGeometry.dropoffsPath}
                  fill="none"
                  stroke="#fbbf24"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              )}

              {chartGeometry.revenuePoints.map((point, index) => (
                <g key={`rev-point-${index}`}>
                  <circle cx={point.x} cy={point.y} r="4.5" fill="#38bdf8" />
                  <title>{`${point.label} • Revenue: ${fmtCurrency(
                    point.value
                  )}`}</title>
                </g>
              ))}

              {chartGeometry.pickupsPoints.map((point, index) => (
                <g key={`pickup-point-${index}`}>
                  <circle cx={point.x} cy={point.y} r="4.5" fill="#34d399" />
                  <title>{`${point.label} • Pickups: ${point.value}`}</title>
                </g>
              ))}

              {chartGeometry.dropoffsPoints.map((point, index) => (
                <g key={`dropoff-point-${index}`}>
                  <circle cx={point.x} cy={point.y} r="4.5" fill="#fbbf24" />
                  <title>{`${point.label} • Dropoffs: ${point.value}`}</title>
                </g>
              ))}

              {chartGeometry.tickIndexes.map((index) => {
                const point = chartGeometry.pickupsPoints[index];
                const label =
                  series[index]?.short_label || series[index]?.label || "";

                return (
                  <g key={`tick-${index}`}>
                    <line
                      x1={point.x}
                      y1={chartGeometry.margin.top + chartGeometry.plotHeight}
                      x2={point.x}
                      y2={
                        chartGeometry.margin.top + chartGeometry.plotHeight + 6
                      }
                      stroke="#71717a"
                      strokeWidth="1"
                    />
                    <text
                      x={point.x}
                      y={chartGeometry.margin.top + chartGeometry.plotHeight + 20}
                      textAnchor="middle"
                      fontSize="11"
                      fill="#a1a1aa"
                    >
                      {label}
                    </text>
                  </g>
                );
              })}
            </svg>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Dashboard() {
  const { setPageError, pageError } = useAuth();

  const API_ORIGIN = useMemo(() => {
    return process.env.REACT_APP_API_ORIGIN || "https://new.cajuu.app";
  }, []);

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [loadPlan, setLoadPlan] = useState(null);

  // Filters
  const [rangeKey, setRangeKey] = useState("last_7_days");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  // Driver filter custom
  const [drivers, setDrivers] = useState([]);
  const [driverSearch, setDriverSearch] = useState("");
  const [selectedDriver, setSelectedDriver] = useState("all");
  const [showDriversList, setShowDriversList] = useState(false);
  const driverSearchRef = useRef(null);

  // Support snake_case or camelCase from backend
  const getItems = (lt) => {
    if (!lt) return [];
    if (Array.isArray(lt.load_items)) return lt.load_items;
    if (Array.isArray(lt.loadItems)) return lt.loadItems;
    return [];
  };

  const roleId = data?.user_role_id ?? null;

  const currentRangeLabel = useMemo(() => {
    return getRangeLabel(rangeKey);
  }, [rangeKey]);

  const buildServerParams = () => {
    const params = {
      range: rangeKey,
    };

    if (statusFilter !== "all") {
      params.load_status_id = statusFilter;
    }

    if (selectedDriver !== "all") {
      params.driver_id = selectedDriver;
    }

    return params;
  };

  const fetchDrivers = async () => {
    try {
      const token = getAuthToken();
      if (!token) return;

      const res = await axios.get(`${API_ORIGIN}/api/driver`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Screen-Name": "/Load",
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      });

      const list =
        (Array.isArray(res?.data?.data?.data) && res.data.data.data) ||
        (Array.isArray(res?.data?.data) && res.data.data) ||
        (Array.isArray(res?.data) && res.data) ||
        [];

      const mapped = list
        .map((d) => {
          const first = d?.user?.first_name || "";
          const last = d?.user?.last_name || "";
          const fullName = `${first} ${last}`.trim();
          const label = fullName || d?.user?.email || `Driver #${d?.id ?? ""}`;

          return {
            id: String(d.id),
            name: label,
          };
        })
        .sort((a, b) => String(a.name).localeCompare(String(b.name)));

      setDrivers(mapped);
    } catch (error) {
      setDrivers([]);
    }
  };

  // Fetch dashboard principal
  useEffect(() => {
    let mounted = true;

    const fetchDashboard = async () => {
      try {
        setLoading(true);

        const token = getAuthToken();
        if (!token) {
          throw new Error("Missing auth token in localStorage.");
        }

        const res = await axios.get(`${API_ORIGIN}/api/dashboard`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Screen-Name": "/Load",
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          params: buildServerParams(),
          withCredentials: false,
        });

        const payload = res?.data;
        const apiData = payload && payload.data ? payload.data : payload;

        if (mounted) {
          setData(apiData);
        }
      } catch (err) {
        console.error(err);
        const message =
          err?.response?.data?.message ||
          err?.message ||
          "Failed to load dashboard data.";
        toast.error(message);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchDashboard();

    return () => {
      mounted = false;
    };
  }, [API_ORIGIN, rangeKey, statusFilter, selectedDriver]);

  // Fetch drivers for non-driver users
  useEffect(() => {
    if (roleId === null) return;
    if (roleId === 2) return;

    fetchDrivers();
  }, [roleId]);

  // Fetch Load Diagram
  useEffect(() => {
    if (!(roleId === 2 || roleId === 4)) {
      setLoadPlan(null);
      return;
    }

    let mounted = true;

    const fetchDiagram = async () => {
      try {
        const token = getAuthToken();
        if (!token) return;

        const res = await axios.get(`${API_ORIGIN}/api/load-planning/diagram`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Screen-Name": "/Load",
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        });

        if (mounted) {
          if (res?.data?.data) {
            setLoadPlan(res.data.data);
          } else {
            setLoadPlan(null);
          }
        }
      } catch (error) {
        console.error("Failed to fetch load diagram", error);
      }
    };

    fetchDiagram();

    return () => {
      mounted = false;
    };
  }, [API_ORIGIN, roleId]);

  useEffect(() => {
    if (pageError) {
      toast.error(pageError);
      setPageError(null);
    }
  }, [pageError, setPageError]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        driverSearchRef.current &&
        !driverSearchRef.current.contains(event.target)
      ) {
        setShowDriversList(false);

        if (driverSearch.trim() === "" && selectedDriver !== "all") {
          setTimeout(() => {
            setSelectedDriver("all");
          }, 10);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [driverSearch, selectedDriver]);

  useEffect(() => {
    if (driverSearch.trim() === "" && selectedDriver !== "all") {
      setSelectedDriver("all");
    }
  }, [driverSearch, selectedDriver]);

  const filteredDrivers = useMemo(() => {
    return drivers.filter((d) =>
      d.name.toLowerCase().includes(driverSearch.toLowerCase())
    );
  }, [driverSearch, drivers]);

  const handleDriverSelect = (driverId, driverName) => {
    setSelectedDriver(driverId);
    setDriverSearch(driverName);
    setShowDriversList(false);
  };

  const clearDriverFilter = () => {
    setSelectedDriver("all");
    setDriverSearch("");
    setShowDriversList(false);
  };

  const clearFilters = () => {
    setRangeKey("last_7_days");
    setStatusFilter("all");
    setSelectedDriver("all");
    setDriverSearch("");
    setShowDriversList(false);
    setShowAdvancedFilters(false);

    toast.success("Filters cleared.");
  };

  const handleDriverBlur = () => {
    setTimeout(() => {
      setShowDriversList(false);
    }, 200);
  };

  // Flags para saber se o backend enviou cada KPI
  const hasAssignedLoadsKpi =
    data &&
    Object.prototype.hasOwnProperty.call(data, "assigned_load_transports");
  const hasTotalLoadsKpi =
    data && Object.prototype.hasOwnProperty.call(data, "total_loads");
  const hasTotalRevenueKpi =
    data && Object.prototype.hasOwnProperty.call(data, "total_revenue");
  const hasRecentQuotesKpi =
    data && Object.prototype.hasOwnProperty.call(data, "recent_quotes");
  const hasRecentDeliveriesKpi =
    data && Object.prototype.hasOwnProperty.call(data, "recent_deliveries");

  // Rows for Recent Loads
  const recentLoadsRows = useMemo(() => {
    if (!data || !Array.isArray(data.recent_loads)) return [];

    return data.recent_loads.flatMap((lt) => {
      const items = getItems(lt);

      if (!items.length) {
        return [
          {
            id: `${lt.id}-x`,
            loadId: lt.id,
            pickup: `${lt?.pickup_city ?? ""}/${lt?.pickup_state ?? ""}`,
            delivery: `${lt?.delivery_city ?? ""}/${lt?.delivery_state ?? ""}`,
            rate: fmtCurrency(Number(lt?.payment_rate ?? 0)),
            vehicle: "-",
          },
        ];
      }

      return items.map((item) => ({
        id: `${lt.id}-${item?.id ?? "x"}`,
        loadId: lt.id,
        pickup: `${lt?.pickup_city ?? ""}/${lt?.pickup_state ?? ""}`,
        delivery: `${lt?.delivery_city ?? ""}/${lt?.delivery_state ?? ""}`,
        rate: fmtCurrency(Number(lt?.payment_rate ?? 0)),
        vehicle: `${item?.year ?? ""} ${item?.make ?? ""} ${
          item?.model ?? ""
        }`.trim(),
      }));
    });
  }, [data]);

  // Rows for Recent Picked Up
  const recentPickedUpRows = useMemo(() => {
    if (!data || !Array.isArray(data.recent_picked_up)) return [];

    return data.recent_picked_up.flatMap((lt) => {
      const items = getItems(lt);

      if (!items.length) {
        return [
          {
            id: `${lt.id}-x`,
            loadId: lt.id,
            pickup: `${lt?.pickup_city ?? ""}/${lt?.pickup_state ?? ""}`,
            vehicle: "-",
          },
        ];
      }

      return items.map((item) => ({
        id: `${lt.id}-${item?.id ?? "x"}`,
        loadId: lt.id,
        pickup: `${lt?.pickup_city ?? ""}/${lt?.pickup_state ?? ""}`,
        vehicle: `${item?.year ?? ""} ${item?.make ?? ""} ${
          item?.model ?? ""
        }`.trim(),
      }));
    });
  }, [data]);

  // Rows for Recent Dropped Off
  const recentDroppedOffRows = useMemo(() => {
    if (!data || !Array.isArray(data.recent_dropped_off)) return [];

    return data.recent_dropped_off.flatMap((lt) => {
      const items = getItems(lt);

      if (!items.length) {
        return [
          {
            id: `${lt.id}-x`,
            loadId: lt.id,
            delivery: `${lt?.delivery_city ?? ""}/${lt?.delivery_state ?? ""}`,
            vehicle: "-",
          },
        ];
      }

      return items.map((item) => ({
        id: `${lt.id}-${item?.id ?? "x"}`,
        loadId: lt.id,
        delivery: `${lt?.delivery_city ?? ""}/${lt?.delivery_state ?? ""}`,
        vehicle: `${item?.year ?? ""} ${item?.make ?? ""} ${
          item?.model ?? ""
        }`.trim(),
      }));
    });
  }, [data]);

  // Cells as full-size anchors
  const cellLink = (url, content) => (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="block w-full h-full py-2"
      title="Open load details"
    >
      {content}
    </a>
  );

  const recentLoadsColumns = useMemo(
    () => [
      {
        accessorKey: "vehicle",
        header: "Vehicle",
        cell: ({ row }) => {
          const url = `https://view.cajuu.app/loads/public/${row.original.loadId}`;
          return cellLink(url, row.original.vehicle || "-");
        },
      },
      {
        accessorKey: "pickup",
        header: "Pickup",
        cell: ({ row }) => {
          const url = `https://view.cajuu.app/loads/public/${row.original.loadId}`;
          return cellLink(url, row.original.pickup || "-");
        },
      },
      {
        accessorKey: "delivery",
        header: "Delivery",
        cell: ({ row }) => {
          const url = `https://view.cajuu.app/loads/public/${row.original.loadId}`;
          return cellLink(url, row.original.delivery || "-");
        },
      },
      {
        accessorKey: "rate",
        header: "Rate",
        cell: ({ row }) => {
          const url = `https://view.cajuu.app/loads/public/${row.original.loadId}`;
          return cellLink(url, row.original.rate || "-");
        },
      },
    ],
    []
  );

  const recentPickedUpColumns = useMemo(
    () => [
      {
        accessorKey: "vehicle",
        header: "Vehicle",
        cell: ({ row }) => {
          const url = `https://view.cajuu.app/loads/public/${row.original.loadId}`;
          return cellLink(url, row.original.vehicle || "-");
        },
      },
      {
        accessorKey: "pickup",
        header: "Pickup",
        cell: ({ row }) => {
          const url = `https://view.cajuu.app/loads/public/${row.original.loadId}`;
          return cellLink(url, row.original.pickup || "-");
        },
      },
    ],
    []
  );

  const recentDroppedOffColumns = useMemo(
    () => [
      {
        accessorKey: "vehicle",
        header: "Vehicle",
        cell: ({ row }) => {
          const url = `https://view.cajuu.app/loads/public/${row.original.loadId}`;
          return cellLink(url, row.original.vehicle || "-");
        },
      },
      {
        accessorKey: "delivery",
        header: "Delivery",
        cell: ({ row }) => {
          const url = `https://view.cajuu.app/loads/public/${row.original.loadId}`;
          return cellLink(url, row.original.delivery || "-");
        },
      },
    ],
    []
  );

  // KPI values
  const assignedLoadsVal = data?.assigned_load_transports ?? 0;
  const totalLoadsVal = data?.total_loads ?? 0;
  const totalRevenueVal = data?.total_revenue ?? 0;
  const recentQuotesVal = data?.recent_quotes ?? 0;
  const recentDeliveriesVal = data?.recent_deliveries ?? 0;

  const kpiNote = useMemo(() => {
    const parts = [currentRangeLabel];

    if (statusFilter !== "all") {
      const statusLabel =
        STATUS_OPTIONS.find((item) => item.value === statusFilter)?.label ||
        "Selected Status";
      parts.push(statusLabel);
    }

    if (selectedDriver !== "all" && driverSearch.trim()) {
      parts.push(driverSearch.trim());
    }

    return `Filtered by ${parts.join(" • ")}.`;
  }, [currentRangeLabel, statusFilter, selectedDriver, driverSearch]);

  return (
    <AuthenticatedLayout>
      <div className="py-6 sm:py-12">
        <div className="px-4 sm:px-6 lg:px-8 max-w-screen-2xl mx-auto">
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-white mb-4">
            Dashboard
          </h1>

          {/* KPIs */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {hasAssignedLoadsKpi && (
              <div className="flex flex-col">
                <DashboardColumn
                  title="Assigned Loads"
                  value={loading ? "…" : assignedLoadsVal}
                  Icon={Notebook}
                  iconBg="bg-blue-500"
                />
                <p className="text-xs text-gray-500 text-muted-foreground mt-1">
                  {kpiNote}
                </p>
              </div>
            )}

            {hasTotalLoadsKpi && (
              <div className="flex flex-col">
                <DashboardColumn
                  title="Total Loads"
                  value={loading ? "…" : totalLoadsVal}
                  Icon={TbNotes}
                  iconBg="bg-green-500"
                />
                <p className="text-xs text-gray-500 text-muted-foreground mt-1">
                  {kpiNote}
                </p>
              </div>
            )}

            {hasTotalRevenueKpi && (
              <div className="flex flex-col">
                <DashboardColumn
                  title="Total Revenue"
                  value={loading ? "…" : fmtCurrency(Number(totalRevenueVal))}
                  Icon={TbNotes}
                  iconBg="bg-green-500"
                />
                <p className="text-xs text-gray-500 text-muted-foreground mt-1">
                  {kpiNote}
                </p>
              </div>
            )}

            {hasRecentQuotesKpi && (
              <div className="flex flex-col">
                <DashboardColumn
                  title="Recent Quotes"
                  value={loading ? "…" : recentQuotesVal}
                  Icon={TbNotes}
                  iconBg="bg-green-500"
                />
                <p className="text-xs text-gray-500 text-muted-foreground mt-1">
                  {kpiNote}
                </p>
              </div>
            )}

            {hasRecentDeliveriesKpi && (
              <div className="flex flex-col">
                <DashboardColumn
                  title="Recent Deliveries"
                  value={loading ? "…" : recentDeliveriesVal}
                  Icon={TbNotes}
                  iconBg="bg-green-500"
                />
                <p className="text-xs text-gray-500 text-muted-foreground mt-1">
                  {kpiNote}
                </p>
              </div>
            )}
          </div>

          {/* Filters */}
          <div className="bg-[#1c1b19] p-4 sm:p-6 rounded-xl shadow-sm text-white border border-zinc-600 mb-6">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between mb-4">
              <div>
                <h2 className="text-lg sm:text-xl font-bold">Filters</h2>
                <p className="text-sm text-gray-400 mt-1">
                  Quick date ranges stay visible. Advanced filters start
                  collapsed.
                </p>
              </div>

              <button
                onClick={clearFilters}
                className="text-sm text-gray-400 hover:text-white flex items-center gap-1"
                type="button"
              >
                <TbX className="w-4 h-4" />
                Clear Filters
              </button>
            </div>

            <div className="flex flex-wrap gap-2 mb-4">
              {RANGE_OPTIONS.map((range) => {
                const isActive = rangeKey === range.value;

                return (
                  <button
                    key={range.value}
                    type="button"
                    onClick={() => setRangeKey(range.value)}
                    className={`px-3 py-2 rounded-lg text-sm border transition-all ${
                      isActive
                        ? "bg-amber-500 text-black border-amber-500"
                        : "bg-transparent text-white border-zinc-600 hover:border-amber-500 hover:text-amber-300"
                    }`}
                  >
                    {range.label}
                  </button>
                );
              })}
            </div>

            <button
              type="button"
              onClick={() => setShowAdvancedFilters((prev) => !prev)}
              className="w-full flex items-center justify-between rounded-xl border border-zinc-700 bg-zinc-900/30 px-4 py-3 text-left hover:bg-zinc-800/40 transition-colors"
            >
              <div className="flex items-center gap-3">
                <TbAdjustmentsHorizontal className="text-amber-400 text-lg" />
                <div>
                  <div className="text-sm font-medium text-white">
                    Advanced Filters
                  </div>
                  <div className="text-xs text-gray-400">
                    Search Driver and Load Status
                  </div>
                </div>
              </div>

              <TbChevronDown
                className={`text-white text-lg transition-transform duration-200 ${
                  showAdvancedFilters ? "rotate-180" : ""
                }`}
              />
            </button>

            {showAdvancedFilters && (
              <div
                className={`mt-4 grid grid-cols-1 ${
                  roleId === 2
                    ? "md:grid-cols-1 lg:grid-cols-1"
                    : "md:grid-cols-2 lg:grid-cols-2"
                } gap-4`}
              >
                {roleId !== 2 && (
                  <div className="relative" ref={driverSearchRef}>
                    <label className="block text-sm text-gray-400 mb-2">
                      Driver
                    </label>

                    <div className="relative">
                      <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                        <TbUser className="text-white text-lg" />
                      </div>

                      <input
                        type="text"
                        placeholder="Search drivers..."
                        value={driverSearch}
                        onChange={(e) => {
                          setDriverSearch(e.target.value);
                          if (e.target.value.trim() !== "") {
                            setShowDriversList(true);
                          }
                        }}
                        onFocus={() => setShowDriversList(true)}
                        onBlur={handleDriverBlur}
                        className="w-full pl-10 pr-10 bg-transparent placeholder:text-white py-2 sm:py-3 text-sm border text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                      />

                      {(driverSearch || selectedDriver !== "all") && (
                        <button
                          onClick={clearDriverFilter}
                          className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-white"
                          type="button"
                        >
                          <TbX className="w-4 h-4" />
                        </button>
                      )}
                    </div>

                    {showDriversList && (
                      <div className="absolute z-10 mt-2 w-full bg-[#1c1b19] border border-zinc-600 rounded-lg overflow-hidden shadow-lg">
                        <div className="max-h-48 overflow-y-auto">
                          <div
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => handleDriverSelect("all", "")}
                            className="px-4 py-3 cursor-pointer hover:bg-zinc-800/50 transition-colors flex items-center justify-between border-b border-zinc-600"
                          >
                            <div className="flex items-center gap-2 min-w-0 flex-1">
                              <span className="px-2 py-1 text-center text-white text-sm">
                                All Drivers
                              </span>
                            </div>

                            {selectedDriver === "all" && (
                              <TbCheck className="text-amber-500 flex-shrink-0 ml-2" />
                            )}
                          </div>

                          {filteredDrivers.length > 0 ? (
                            <>
                              {filteredDrivers.map((d) => (
                                <div
                                  key={d.id}
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={() =>
                                    handleDriverSelect(d.id, d.name)
                                  }
                                  className="px-4 py-3 cursor-pointer hover:bg-zinc-800/50 transition-colors flex items-center justify-between border-b border-zinc-600 last:border-b-0"
                                >
                                  <div className="flex items-center gap-2 min-w-0 flex-1">
                                    <span
                                      className="px-2 py-1 text-center text-white text-sm"
                                      title={d.name}
                                    >
                                      {d.name}
                                    </span>
                                  </div>

                                  {selectedDriver === d.id && (
                                    <TbCheck className="text-amber-500 flex-shrink-0 ml-2" />
                                  )}
                                </div>
                              ))}

                              <div className="px-4 py-2 text-xs text-gray-400 border-t border-zinc-600 bg-zinc-900/50">
                                {driverSearch
                                  ? `Found ${filteredDrivers.length} of ${drivers.length} drivers`
                                  : `Showing all ${filteredDrivers.length} drivers`}
                              </div>
                            </>
                          ) : (
                            <div className="px-4 py-3 text-center text-gray-400 text-sm">
                              No drivers found
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div>
                  <label className="block text-sm text-gray-400 mb-2">
                    Load Status
                  </label>

                  <div className="relative">
                    <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                      <TbCalendar className="text-white text-lg" />
                    </div>

                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      className="w-full pl-10 pr-12 bg-transparent text-white py-2 sm:py-3 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all appearance-none"
                    >
                      {STATUS_OPTIONS.map((opt) => (
                        <option
                          key={opt.value}
                          value={opt.value}
                          className="bg-[#1c1b19]"
                        >
                          {opt.label}
                        </option>
                      ))}
                    </select>

                    <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
                      <TbChevronDown className="text-white text-lg" />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Dynamic Chart */}
          <DashboardTrendChart chart={data?.chart_data} />

          {/* Trailer Diagram */}
          {loadPlan && (roleId === 2 || roleId === 4 || roleId === 1) && (
            <div className="mb-8">
              <TrailerDiagram planData={loadPlan} />
            </div>
          )}

          {/* Tables */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 col-span-3">
            {/* Recent Loads */}
            <div className="mt-6 m-2 sm:mt-10 w-full lg:col-span-2">
              <h2 className="text-xl font-semibold tracking-tight text-white mb-1">
                Recent Loads
              </h2>
              <p className="text-sm text-gray-500 text-muted-foreground mb-4">
                Filtered by {currentRangeLabel}
                {statusFilter !== "all"
                  ? ` • ${
                      STATUS_OPTIONS.find((item) => item.value === statusFilter)
                        ?.label || "Selected Status"
                    }`
                  : ""}
                {selectedDriver !== "all" && driverSearch.trim()
                  ? ` • ${driverSearch.trim()}`
                  : ""}
                .
              </p>

              <div className="overflow-x-auto">
                <DataTable
                  columns={recentLoadsColumns}
                  initialData={loading ? [] : recentLoadsRows}
                />
              </div>
            </div>

            {/* Recent Picked Up */}
            <div className="mt-6 m-2 w-full sm:mt-10 lg:col-span-1">
              <h2 className="text-xl font-semibold tracking-tight text-white mb-1">
                Recent Picked Up
              </h2>
              <p className="text-sm text-gray-500 text-muted-foreground mb-4">
                Filtered by {currentRangeLabel}
                {statusFilter !== "all"
                  ? ` • ${
                      STATUS_OPTIONS.find((item) => item.value === statusFilter)
                        ?.label || "Selected Status"
                    }`
                  : ""}
                {selectedDriver !== "all" && driverSearch.trim()
                  ? ` • ${driverSearch.trim()}`
                  : ""}
                .
              </p>

              <div className="overflow-x-auto">
                <DataTable
                  columns={recentPickedUpColumns}
                  initialData={loading ? [] : recentPickedUpRows}
                />
              </div>
            </div>

            {/* Recent Dropped Off */}
            <div className="mt-6 w-full m-2 sm:mt-10 lg:col-span-1">
              <h2 className="text-xl font-semibold tracking-tight text-white mb-1">
                Recent Dropped Off
              </h2>
              <p className="text-sm text-gray-500 text-muted-foreground mb-4">
                Filtered by {currentRangeLabel}
                {statusFilter !== "all"
                  ? ` • ${
                      STATUS_OPTIONS.find((item) => item.value === statusFilter)
                        ?.label || "Selected Status"
                    }`
                  : ""}
                {selectedDriver !== "all" && driverSearch.trim()
                  ? ` • ${driverSearch.trim()}`
                  : ""}
                .
              </p>

              <div className="overflow-x-auto">
                <DataTable
                  columns={recentDroppedOffColumns}
                  initialData={loading ? [] : recentDroppedOffRows}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </AuthenticatedLayout>
  );
}